import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import type { EmptyFieldWarning } from "@/features/BroadcastV2/emptySubjectPreview";

interface EmptyPreviewTextConfirmModalProps {
  open: boolean;
  warnings: EmptyFieldWarning[];
  isSubmitting: boolean;
  /** "Send anyway" / "Schedule anyway" depending on send mode. */
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Soft confirmation shown before send/schedule when a broadcast email's
 * selected frame has blank preview text (FOG-2048). A blank subject line
 * blocks the send before this opens.
 */
export function EmptyPreviewTextConfirmModal({
  open,
  warnings,
  isSubmitting,
  confirmLabel,
  onCancel,
  onConfirm,
}: EmptyPreviewTextConfirmModalProps) {
  const isSingle = warnings.length === 1 && warnings[0]?.label === "";
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="empty-preview-text-title"
    >
      <DialogTitle id="empty-preview-text-title">
        Missing preview text
      </DialogTitle>
      <DialogContent>
        {isSingle ? (
          <Typography variant="body2" color="text.secondary">
            This email has no preview text.
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 4 }}>
            {warnings.map((w) => (
              <Typography
                key={w.label}
                component="li"
                variant="body2"
                color="text.secondary"
              >
                {w.label}: no preview text
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 2, justifyContent: "flex-end", px: 4, pb: 3 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
